import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { ChevronDown, LogOut, Moon, Sun } from "lucide-react";

import { useTheme } from "../../context/ThemeContext";
import { Breadcrumb, Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger, cn, focusRing } from "../../components/ui";
import { ADMIN_NAV_ITEMS } from "./AdminSidebar";

export default function AdminTopbar({ user, onLogout }) {
  const { pathname } = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const displayName = user?.username || "Admin";
  const initials = Array.from(displayName).slice(0, 2).join("").toUpperCase();
  const activeSection = ADMIN_NAV_ITEMS.find(
    ({ path }) => pathname === path || pathname.startsWith(`${path}/`)
  );
  const sectionTitle = activeSection?.label || "Dashboard";
  const crumbs = useMemo(() => [{ label: sectionTitle }], [sectionTitle]);

  return (
    <div className="flex h-full items-center justify-between gap-3">
      <Breadcrumb items={crumbs} className="flex-1" />

      <Menu modal={false}>
        <MenuTrigger asChild>
          <button
            type="button"
            aria-label={`Account menu: ${displayName}`}
            className={cn(
              "group flex h-9 items-center gap-2 rounded-control pl-1 pr-1.5 text-left transition-colors hover:bg-hover max-md:h-11",
              focusRing
            )}
          >
            <span aria-hidden="true" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-surface-sunken text-meta font-semibold text-secondary">
              {initials}
            </span>
            <span aria-hidden="true" className="hidden min-w-0 lg:block">
              <span className="block max-w-40 truncate text-body-sm font-medium text-foreground">{displayName}</span>
            </span>
            <ChevronDown aria-hidden="true" className="hidden h-3.5 w-3.5 shrink-0 text-tertiary transition-transform group-data-[state=open]:rotate-180 sm:block" />
          </button>
        </MenuTrigger>
        <MenuContent className="w-64">
          <MenuLabel className="py-2">
            <span className="block truncate text-body-sm font-medium text-foreground">{displayName}</span>
          </MenuLabel>
          <MenuSeparator />
          <MenuItem
            icon={isDark ? Sun : Moon}
            onSelect={(event) => {
              event.preventDefault();
              toggleTheme();
            }}
          >
            {isDark ? "Use light theme" : "Use dark theme"}
          </MenuItem>
          <MenuSeparator />
          <MenuItem tone="danger" icon={LogOut} onSelect={onLogout}>
            Log out
          </MenuItem>
        </MenuContent>
      </Menu>
    </div>
  );
}
